// src/App.js
import React, { useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './App.css';

import { AuthContext } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import Layout from './components/layout/Layout';
import GlobalSearch from './components/GlobalSearch';

// Páginas
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TicketList from './pages/TicketList';
import TicketNew from './pages/TicketNew';
import TicketDetail from './pages/TicketDetail';
import Departments from './pages/Departments';
import InviteAgent from './pages/InviteAgent';
import AgentManager from './pages/AgentManager';
import UserManager from './pages/UserManager';
import Holidays from './pages/Holidays';
import BusinessHours from './pages/BusinessHours';
import Guardias from './pages/Guardias';
import InvitationManager from './pages/InvitationManager';
import AuthorizationsPending from './pages/AuthorizationsPending';
import UserMigration from './pages/UserMigration';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CannedResponses from './pages/CannedResponses';
import DirectorioCorreos from './pages/DirectorioCorreos';
import SlaConfig from './pages/SlaConfig';
import LeaderReports from './pages/LeaderReports';
import CsatFeedback from './pages/CsatFeedback';
import ProductionReports from './pages/ProductionReports';
import UnitPolicies from './pages/UnitPolicies';
import Chat from './pages/Chat';
import ChatPolicies from './pages/ChatPolicies';
import SupportInbox from './pages/SupportInbox';
import SupportRouting from './pages/SupportRouting';
import SupportKnowledge from './pages/SupportKnowledge';
import SupportFaqs from './pages/SupportFaqs';
import SupportSla from './pages/SupportSla';
import SupportReports from './pages/SupportReports';
import AdminHealth from './pages/AdminHealth';
import Hechos from './pages/Hechos';
import HechoNuevo from './pages/HechoNuevo';
import HechoDetalle from './pages/HechoDetalle';
import HechosRiesgo from './pages/HechosRiesgo';
import HechosAdmin from './pages/HechosAdmin';
// Salas de juntas: mini sistema bajo /salas con su propio login y layout.
import SalasLogin from './pages/salas/SalasLogin';
import SalasShell from './pages/salas/SalasShell';
import SalasHome from './pages/salas/SalasHome';
import SalasQr from './pages/salas/SalasQr';
import SalasAdmin from './pages/salas/SalasAdmin';
// Revalidación de datos Vale Amigo: formulario público (sin login) + admin en el Desk.
import RevalForm from './pages/reval/RevalForm';
import RevalAdmin from './pages/reval/RevalAdmin';

function RequireAuth({ children }) {
    const { token } = useContext(AuthContext);
    if (!token) return <Navigate to="/login" replace />;
    return children;
}

function RequireRole({ roles, children, allowAgentAuthorizer = false, allowLeader = false, allowLeaderRole = false, requireSupportHandler = false }) {
    const { user } = useContext(AuthContext);
    if (!user) return <Navigate to="/dashboard" replace />;
    // Si la ruta exige canHandleSupport: admin siempre pasa; leader y agent
    // requieren el flag explícito activado en /agents. (Antes los leaders pasaban
    // automáticamente, pero eso exponía el módulo a leaders que no atienden Presico.)
    if (requireSupportHandler) {
        if (user.role === 'admin') return children;
        if ((user.role === 'leader' || user.role === 'agent') && user.canHandleSupport) return children;
        return <Navigate to="/dashboard" replace />;
    }
    if (roles.includes(user.role)) return children;
    if (allowLeaderRole && user.role === 'leader') return children;
    if (allowAgentAuthorizer && user.role === 'agent' && user.canAuthorize) return children;
    if (allowLeader && user.role === 'agent' && user.isLeader) return children;
    return <Navigate to="/dashboard" replace />;
}

/**
 * Puerta del módulo de Hechos Operativos. A propósito NO reutiliza RequireRole:
 * aquí los permisos no se derivan del rol de CaprepaDesk sino del puesto del
 * intranet contra el catálogo de la política, y eso ya viene resuelto en
 * `user.incidents` desde /auth/me. Es solo para no pintar una pantalla que el
 * servidor va a rechazar; la autorización de verdad vive en el backend.
 */
function RequireIncidents({ children, requireReport = false }) {
    const { user } = useContext(AuthContext);
    if (!user) return <Navigate to="/dashboard" replace />;
    if (!user.incidents?.hasAccess) return <Navigate to="/dashboard" replace />;
    if (requireReport && !user.incidents?.canReport) return <Navigate to="/hechos" replace />;
    return children;
}

// Puerta del Directorio de correos. Acceso por lista blanca (canMailDirectory)
// resuelto en /auth/me → user.mailDirectory.hasAccess. Solo evita pintar una
// pantalla que el backend rechazaría; la autorización real vive en el servidor.
function RequireMailDirectory({ children }) {
    const { user } = useContext(AuthContext);
    if (!user) return <Navigate to="/dashboard" replace />;
    if (!user.mailDirectory?.hasAccess) return <Navigate to="/dashboard" replace />;
    return children;
}

export default function App() {
    const { token } = useContext(AuthContext);

    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-bs-theme', theme);
        localStorage.setItem('theme', theme);

        if (theme === 'dark') {
            // Variables semánticas (theme.css)
            root.style.setProperty('--c-bg',                  '#0d1117');
            root.style.setProperty('--c-card',                '#161b22');
            root.style.setProperty('--c-card-header-border',  '#21262d');
            root.style.setProperty('--c-border',              '#30363d');
            root.style.setProperty('--c-input-bg',            '#0d1117');
            root.style.setProperty('--c-msg-bg',              '#1c2128');
            root.style.setProperty('--c-text-1',              '#e6edf3');
            root.style.setProperty('--c-text-2',              '#c9d1d9');
            root.style.setProperty('--c-text-3',              '#8b949e');
            root.style.setProperty('--c-text-4',              '#6e7681');
            root.style.setProperty('--c-status-open-bg',      '#1a2a4a');
            root.style.setProperty('--c-status-progress-bg',  '#2a1f0a');
            root.style.setProperty('--c-status-closed-bg',    '#1a1f26');
            root.style.setProperty('--c-priority-low-bg',     '#0a1f15');
            root.style.setProperty('--c-priority-urgent-bg',  '#2a1010');
            root.style.setProperty('--c-error-bg',            '#2a1010');
            root.style.setProperty('--c-error-border',        '#6b1010');
            root.style.setProperty('--c-error-text',          '#f87171');
            root.style.setProperty('--c-ticket-hover-bg',     '#1a2540');
            root.style.setProperty('--c-ticket-hover-border', '#4a7dbc');
            // Variables de marca (brand.css)
            root.style.setProperty('--bg-page',     '#0d1117');
            root.style.setProperty('--card-bg',     '#161b22');
            root.style.setProperty('--border-soft', '#30363d');
            root.style.setProperty('--text-strong', '#e6edf3');
            root.style.setProperty('--text-muted',  '#8b949e');
            // Bootstrap body
            root.style.setProperty('--bs-body-bg',    '#0d1117');
            root.style.setProperty('--bs-body-color', '#e6edf3');
            root.style.background = '#0d1117';
            document.body.style.background = '#0d1117';
        } else {
            // Limpiar todos los overrides para volver al CSS base
            [
                '--c-bg','--c-card','--c-card-header-border','--c-border',
                '--c-input-bg','--c-msg-bg','--c-text-1','--c-text-2',
                '--c-text-3','--c-text-4','--c-status-open-bg','--c-status-progress-bg',
                '--c-status-closed-bg','--c-priority-low-bg','--c-priority-urgent-bg',
                '--c-error-bg','--c-error-border','--c-error-text',
                '--c-ticket-hover-bg','--c-ticket-hover-border',
                '--bg-page','--card-bg','--border-soft','--text-strong','--text-muted',
                '--bs-body-bg','--bs-body-color',
            ].forEach(v => root.style.removeProperty(v));
            root.style.removeProperty('background');
            document.body.style.removeProperty('background');
        }
    }, [theme]);

    const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setShowSearch(v => !v);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    return (
        <NotificationProvider>
        <>
            <GlobalSearch open={showSearch} onClose={() => setShowSearch(false)} />
            <Routes>
                <Route
                    path="/"
                    element={token ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
                />

                {/* Públicas */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Protegidas */}
                <Route
                    path="/dashboard"
                    element={
                        <RequireAuth>
                            {/* ✅ CAMBIO: pasamos props a Layout (aún no lo usas hasta modificar Topbar) */}
                            <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                <Dashboard />
                            </Layout>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/tickets"
                    element={
                        <RequireAuth>
                            <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                <TicketList />
                            </Layout>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/tickets/new"
                    element={
                        <RequireAuth>
                            <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                <TicketNew />
                            </Layout>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/tickets/:id"
                    element={
                        <RequireAuth>
                            <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                <TicketDetail />
                            </Layout>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/departments"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']} allowAgentAuthorizer allowLeaderRole>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <Departments />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/invite"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']} allowAgentAuthorizer allowLeaderRole>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <InviteAgent />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/agents"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']} allowLeaderRole>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <AgentManager />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/usuarios"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <UserManager />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/invitations"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'agent']} allowLeaderRole>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <InvitationManager />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/authorizations"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['authorizer', 'admin']} allowAgentAuthorizer allowLeaderRole>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <AuthorizationsPending />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/migration"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <UserMigration />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/canned"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'agent', 'leader']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <CannedResponses />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/directorio-correos"
                    element={
                        <RequireAuth>
                            <RequireMailDirectory>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <DirectorioCorreos />
                                </Layout>
                            </RequireMailDirectory>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/sla"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']} allowLeader allowAgentAuthorizer allowLeaderRole>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SlaConfig />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/reports"
                    element={<Navigate to="/leader-reports" replace />}
                />
                <Route
                    path="/leader-reports"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <LeaderReports />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />

                <Route
                    path="/csat-feedback"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader', 'agent']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <CsatFeedback />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />

                <Route
                    path="/production-reports"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <ProductionReports />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />

                <Route
                    path="/unit-policies"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <UnitPolicies />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />

                <Route
                    path="/holidays"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <Holidays />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/guardias"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader', 'agent']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <Guardias />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/business-hours"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <BusinessHours />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/chat"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'agent', 'leader']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <Chat />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/chat-policies"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <ChatPolicies />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                {/* ── Hechos Operativos ───────────────────────────────────
                    Módulo aislado: no comparte permisos con el resto del
                    ecosistema, así que no usa RequireRole. El acceso lo decide
                    el servidor (services/incidentAccess) en cada request, y
                    RequireIncidents solo evita pintar la pantalla a quien el
                    /auth/me ya marcó sin acceso. */}
                <Route
                    path="/hechos"
                    element={
                        <RequireAuth>
                            <RequireIncidents>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <Hechos />
                                </Layout>
                            </RequireIncidents>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/hechos/admin"
                    element={
                        <RequireAuth>
                            <RequireIncidents>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <HechosAdmin />
                                </Layout>
                            </RequireIncidents>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/hechos/riesgo"
                    element={
                        <RequireAuth>
                            <RequireIncidents>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <HechosRiesgo />
                                </Layout>
                            </RequireIncidents>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/hechos/nuevo"
                    element={
                        <RequireAuth>
                            <RequireIncidents requireReport>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <HechoNuevo />
                                </Layout>
                            </RequireIncidents>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/hechos/:id"
                    element={
                        <RequireAuth>
                            <RequireIncidents>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <HechoDetalle />
                                </Layout>
                            </RequireIncidents>
                        </RequireAuth>
                    }
                />

                <Route
                    path="/support"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader', 'agent']} requireSupportHandler>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SupportInbox />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/support-routing"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']} requireSupportHandler>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SupportRouting />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/support-knowledge"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']} requireSupportHandler>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SupportKnowledge />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/support-faqs"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']} requireSupportHandler>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SupportFaqs />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/support-sla"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']} requireSupportHandler>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SupportSla />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/support-reports"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin', 'leader']} requireSupportHandler>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <SupportReports />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/admin-health"
                    element={
                        <RequireAuth>
                            <RequireRole roles={['admin']}>
                                <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                    <AdminHealth />
                                </Layout>
                            </RequireRole>
                        </RequireAuth>
                    }
                />
                {/* Salas de juntas (/salas): login propio, sin sidebar; el acceso lo resuelve /api/salas/me */}
                <Route path="/salas/login" element={<SalasLogin />} />
                <Route path="/salas" element={<SalasShell />}>
                    <Route index element={<SalasHome />} />
                    <Route path="qr" element={<SalasQr />} />
                    <Route path="admin" element={<SalasAdmin />} />
                </Route>

                {/* Revalidación de datos Vale Amigo: público (solo número de empleado) y admin dentro del Desk
                    (el permiso real lo decide /api/reval/me: admins del Desk o managers designados) */}
                <Route path="/revalidacion" element={<RevalForm />} />
                <Route
                    path="/revalidacion/admin"
                    element={
                        <RequireAuth>
                            <Layout theme={theme} onToggleTheme={toggleTheme} onOpenSearch={() => setShowSearch(true)}>
                                <RevalAdmin />
                            </Layout>
                        </RequireAuth>
                    }
                />

                <Route path="*" element={<div style={{ padding: 24 }}>Página no encontrada</div>} />
            </Routes>

            <ToastContainer position="bottom-right" />
        </>
        </NotificationProvider>
    );
}